import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import CompactSelectPicker from '../ui/CompactSelectPicker';
import { useTeamsStore } from '../../store/teamsStore';
import { teamIdOfScope } from '../../graph/graphScopes';

/**
 * Team items in the Graph (27.09.2026, D7). Colour stays by type; where an item
 * lives is said in text: "Team · <name>" in the details header, the search rows
 * and a project's item rows, and the toolbar's Scope picker filters the canvas.
 *
 * Team NAMES come only from the user's own roster (useTeamsStore) — never from
 * a record, and never over MCP.
 */

/** `(node) => "Team · Backend" | null` — null for personal/device/workspace items. */
export function useScopeLabel() {
  const { t } = useTranslation();
  const teams = useTeamsStore((s) => s.teams);
  return useCallback(
    (node) => {
      const teamId = teamIdOfScope(node?.scope);
      if (!teamId) return null;
      const team = (teams || []).find((x) => x?.id != null && String(x.id) === teamId);
      return team?.name ? t('graph.scope.team', { name: team.name }) : t('graph.scope.teamUnnamed');
    },
    [t, teams]
  );
}

/** The "where it lives" tag for a team item; renders nothing for anything else. */
export function ScopeTag({ node, className = 'graph-scope-tag' }) {
  const label = useScopeLabel()(node);
  return label ? <span className={className}>{label}</span> : null;
}

/**
 * Toolbar picker: Everything · Personal · one entry per team. Hidden while the
 * user belongs to no team — it would only ever offer "Personal".
 */
export default function GraphScopePicker({ value, onChange }) {
  const { t } = useTranslation();
  const teams = useTeamsStore((s) => s.teams);
  const roster = (teams || []).filter((x) => x?.id != null);
  if (roster.length === 0) return null;
  return (
    <CompactSelectPicker
      className="graph-picker graph-scope-picker"
      menuClassName="graph-picker-menu"
      value={value || ''}
      onChange={(next) => onChange(next || null)}
      options={[
        { value: '', label: t('graph.scope.everything') },
        { value: 'personal', label: t('graph.scope.personal') },
        ...roster.map((team) => ({
          value: `team:${team.id}`,
          label: team.name ? t('graph.scope.team', { name: team.name }) : t('graph.scope.teamUnnamed'),
        })),
      ]}
      menuMinWidth={200}
      aria-label={t('graph.scope.filter')}
    />
  );
}
